<template>
  <BittsSelect
    v-model="pickedCoreRole"
    class="c-select-core-role"
    form-label="Full Access Role"
    data-testid="core-role-selector"
    :disabled="!isCoreSeatSelected"
    :options="coreRoleOptions"
  >
    <template #placeholder>
      <span
        :class="[
          !isCoreSeatSelected
            ? 'text-neutral-text'
            : 'text-neutral-text-placeholder',
        ]"
        class="flex items-center gap-8"
      >
        <FontAwesomeIcon
          v-if="!isCoreSeatSelected"
          class="text-neutral-accent"
          :icon="['fak', 'hidden']"
        />
        <span data-testid="core-role-placeholder" class="mt-1">
          {{ isCoreSeatSelected ? 'Select Full Access Role' : 'Sales Access' }}
        </span>
      </span>
    </template>
  </BittsSelect>
</template>

<script setup lang="ts">
import { BittsSelect } from '@crossbeam/bitts';
import { AddSelectProperties } from '@crossbeam/types';

import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';

import { SALES } from '@/constants/team';
import { CORE, INTEGRATION } from '@/constants/team_v4';
import { useRolesStore } from '@/stores';

const {
  pickedSeat,
  modelValue = null,
  setDefaultSeatOnMount = true,
  initialCoreRole = null,
} = defineProps<{
  pickedSeat: AddSelectProperties<{ description: string }>;
  modelValue: string | null;
  setDefaultSeatOnMount?: boolean;
  initialCoreRole?: string;
}>();
const emit =
  defineEmits<(e: 'update:modelValue', value: string | null) => void>();
const rolesStore = useRolesStore();

const { allRoles, salesSeatRole } = storeToRefs(rolesStore);

const isCoreSeatSelected = computed(() => pickedSeat?.value === CORE);
const coreRoleOptions = computed(() =>
  allRoles.value
    .filter(
      (role) =>
        role.seat_type !== INTEGRATION &&
        (role.seat_type !== SALES || !isCoreSeatSelected.value),
    )
    .map((role) => ({ value: role.id, label: role.name })),
);

const pickedCoreRole = computed({
  get() {
    return modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

/* Syncs pickedCoreRole whenever the seat type changes.
 *
 * Switching TO core:
 *   - null initialCoreRole means the invite flow (no pre-existing user role),
 *     keep null so the user is forced to pick a role explicitly.
 *   - A non-null initialCoreRole that is absent from coreRoleOptions means the
 *     user's current role is not a valid core role (e.g. an integration role
 *     when switching from Integration seat to Full Access). Fall back to the
 *     first available core role rather than showing a raw ID in the dropdown.
 *   - initialCoreRole equal to salesSeatRole means the user was previously on a
 *     Sales seat; don't carry that role over, pick the first core role instead.
 *   - Otherwise restore the user's original core role.
 *
 * Switching AWAY from core to Sales:
 *   Clear pickedCoreRole by replacing it with the sales seat role ID. Without
 *   this, a core role selected before the switch would still be submitted as
 *   crossbeamRoleId on save. Sales seats use salesSeatRole as crossbeamRoleId,
 *   so keeping a stale core role would assign the wrong role server-side.
 *   Falls back to null when no sales role is provisioned (connector orgs).
 *
 * Switching AWAY from core to Integration:
 *   Set pickedCoreRole to null. The role selectors are hidden via v-if when
 *   integration is selected, but the submit handler reads pickedCoreRole as a
 *   fallback. Leaving salesSeatRole.id here would be misleading and could cause
 *   the wrong role to be submitted if the isIntegrationSeat guard were ever
 *   removed or missed. */
watch(
  isCoreSeatSelected,
  (isCore) => {
    if (isCore) {
      const isInitialRoleValid =
        !initialCoreRole ||
        coreRoleOptions.value.some((opt) => opt.value === initialCoreRole);
      if (!isInitialRoleValid || initialCoreRole === salesSeatRole.value?.id) {
        pickedCoreRole.value = coreRoleOptions.value[0]?.value ?? null;
        return;
      }
      pickedCoreRole.value = initialCoreRole;
    } else if (pickedSeat?.value === INTEGRATION) {
      pickedCoreRole.value = null;
    } else {
      pickedCoreRole.value = salesSeatRole.value?.id ?? null;
    }
  },
  { immediate: setDefaultSeatOnMount },
);
</script>
